import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Alert, Input } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { AlertTriangle, Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { StepWrapper } from '@/common/components/ConnectionSteps/StepWrapper'
import {
  PasswordChangeForm,
  PasswordChangeSchema,
} from '../../schemas/ChangePasswordSchema'

type PasswordChangeStepProps = {
  appData: {
    applicationAccountUserId: string
    applicationName: string
    accountName: string
  }
  stepInfo: {
    current: number
    total: number
  }
  onValidationChange?: (isValid: boolean) => void
  onFormChange?: (values: unknown) => void
}

export function PasswordChangeStep({
  appData,
  stepInfo,
  onValidationChange,
  onFormChange,
}: PasswordChangeStepProps) {
  const { t } = useTranslation(['common', 'profile'])
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)

  const getDisplayName = () => {
    if (appData.accountName && appData.accountName.trim()) {
      return `${appData.applicationName} (${appData.accountName})`
    }
    return appData.applicationName
  }

  const hookForm = useForm<PasswordChangeForm>({
    resolver: zodResolver(PasswordChangeSchema()),
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  })

  const isFormValid = hookForm.formState.isValid
  const formValues = hookForm.watch()

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid)
    }
  }, [isFormValid, onValidationChange])

  useEffect(() => {
    if (onFormChange) {
      onFormChange(formValues)
    }
  }, [formValues, onFormChange])

  return (
    <FormProvider {...hookForm}>
      <StepWrapper
        appData={{
          applicationId: '',
          applicationName: appData.applicationName,
          userId: '',
        }}
        stepInfo={stepInfo}
        accountName={appData.accountName}
      >
        <div className="overflow-y-auto pr-2 space-y-4">
          <div className="text-sm text-gray-600 mb-4">
            {t('profile:access.modal.change_password.change.description', {
              displayName: getDisplayName(),
            })}
          </div>

          <div className="w-[50%] space-y-4">
            <Input.Icon
              size="lg"
              name="currentPassword"
              label={t(
                'profile:access.modal.change_password.change.current_password_label',
                { displayName: getDisplayName() },
              )}
              type={showCurrentPassword ? 'text' : 'password'}
              placeholder={t(
                'profile:access.modal.change_password.change.current_password_placeholder',
              )}
              icon={
                showCurrentPassword ? (
                  <Eye
                    className="text-base-500 mt-2"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  />
                ) : (
                  <EyeOff
                    className="text-base-500 mt-2"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  />
                )
              }
              iconPosition="right"
            />

            <Input.Icon
              size="lg"
              name="newPassword"
              label={t(
                'profile:access.modal.change_password.change.new_password_label',
                { displayName: getDisplayName() },
              )}
              type={showNewPassword ? 'text' : 'password'}
              placeholder={t(
                'profile:access.modal.change_password.change.new_password_placeholder',
              )}
              icon={
                showNewPassword ? (
                  <Eye
                    className="text-base-500 mt-2"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  />
                ) : (
                  <EyeOff
                    className="text-base-500 mt-2"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  />
                )
              }
              iconPosition="right"
            />

            <Input.Icon
              size="lg"
              name="confirmNewPassword"
              label={t(
                'profile:access.modal.change_password.change.confirm_new_password_label',
              )}
              type={showConfirmNewPassword ? 'text' : 'password'}
              placeholder={t(
                'profile:access.modal.change_password.change.confirm_new_password_placeholder',
              )}
              icon={
                showConfirmNewPassword ? (
                  <Eye
                    className="text-base-500 mt-2"
                    onClick={() =>
                      setShowConfirmNewPassword(!showConfirmNewPassword)
                    }
                  />
                ) : (
                  <EyeOff
                    className="text-base-500 mt-2"
                    onClick={() =>
                      setShowConfirmNewPassword(!showConfirmNewPassword)
                    }
                  />
                )
              }
              iconPosition="right"
            />
          </div>

          <Alert.Root>
            <Alert.Icon icon={<AlertTriangle />} />
            <Alert.Body>
              <Alert.Description className="whitespace-pre-line">
                {t(
                  'profile:access.modal.change_password.change.warning_message',
                  { displayName: getDisplayName() },
                )}
              </Alert.Description>
            </Alert.Body>
          </Alert.Root>
        </div>
      </StepWrapper>
    </FormProvider>
  )
}
